import styles from "./dropdown-menu.module.css";
import { ReactNode, useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

export type Alignment = "left" | "right" | "center";

interface DropdownMenuProps {
  isVisible: boolean;
  onToggleVisibility: () => void;
  label: ReactNode;
  alignment: Alignment;
  offset?: { x: number; y: number };
  children: ReactNode;
}

export const DropdownMenu = (props: DropdownMenuProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const [dropdownWidth, setDropdownWidth] = useState(0);
  const [labelPosition, setLabelPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  useEffect(() => {
    const updateLabelPosition = () => {
      if (containerRef.current) {
        const offsetX = 10;
        const rect = containerRef.current.getBoundingClientRect();
        setLabelPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }

      if (overlayRef.current) {
        setDropdownWidth(overlayRef.current.offsetWidth);
      }
    };

    if (props.isVisible) {
      updateLabelPosition();
      window.addEventListener("resize", updateLabelPosition);
    }

    return () => {
      window.removeEventListener("resize", updateLabelPosition);
    };
  }, [props.isVisible]);

  useEffect(() => {
    if (props.isVisible && overlayRef.current) {
      setDropdownWidth(overlayRef.current.offsetWidth);
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        props.isVisible &&
        containerRef.current &&
        overlayRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !overlayRef.current.contains(event.target as Node)
      ) {
        props.onToggleVisibility();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props, props.isVisible]);

  let left = labelPosition.left;

  if (props.alignment === "right") {
    left = labelPosition.left + labelPosition.width - dropdownWidth;
  } else if (props.alignment === "center") {
    left = labelPosition.left + labelPosition.width / 2 - dropdownWidth / 2;
  }

  return (
    <>
      <div ref={containerRef} className={styles.dropDownContainer}>
        <button
          className={styles.labelButton}
          onClick={() => {
            props.onToggleVisibility();
          }}
        >
          {props.label}
        </button>
      </div>
      {props.isVisible &&
        createPortal(
          <div
            id="dropdown-portal"
            ref={overlayRef}
            className={`${styles.dropdown} ${
              props.isVisible ? styles.open : ""
            }`}
            style={{
              zIndex: 10000,
              position: "absolute", // Ensure it positions absolutely
              top: labelPosition.top + (props.offset?.y || 0),
              left: left + (props.offset?.x || 0),
            }}
          >
            {props.children}
          </div>,
          document.body
        )}
    </>
  );
};
